// Staff (admin/employee) capability model — the single source of truth shared by server + client.
//
// Applies ONLY to KUVO staff accounts (admin = director = full on everything; employee = per-resource
// grants). It is deliberately NOT used for school coordinators: a coordinator has full control of
// their own school portal via user_school_permissions, never a capability level here.
//
// A resource maps 1:1 to an admin area. A grant gives an employee a LEVEL on a resource; the absence
// of a grant means no access. The director (role "admin") implicitly has "full" on every resource.

export const STAFF_RESOURCES = [
  "overzicht", // admin home dashboard (stats + attention points)
  "scholen", // schools + coordinators + the read-only aanlevering dossier
  "aanbod", // programmes, providers, offerings
  "planning",
  "inschrijvingen",
  "configuratie", // dashboard settings (deadline, keuze threshold, copy-forward)
  "backups",
  "audit", // activity + audit feeds
] as const;

export type StaffResource = (typeof STAFF_RESOURCES)[number];

export function isStaffResource(v: string): v is StaffResource {
  return (STAFF_RESOURCES as readonly string[]).includes(v);
}

// Human labels for the management UI.
export const RESOURCE_LABELS: Record<StaffResource, string> = {
  overzicht: "Overzicht",
  scholen: "Scholen & aanleveringen",
  aanbod: "Aanbod",
  planning: "Planning",
  inschrijvingen: "Inschrijvingen",
  configuratie: "Configuratie",
  backups: "Back-ups",
  audit: "Activiteit & audit",
};

// Ordered low -> high. "none" is the implicit default (no grant row).
export const PERMISSION_LEVELS = ["none", "read", "edit", "full"] as const;
export type PermissionLevel = (typeof PERMISSION_LEVELS)[number];

// Levels that can actually be stored as a grant (a stored grant is never "none" — you delete the row).
export const GRANT_LEVELS = ["read", "edit", "full"] as const;
export type GrantLevel = (typeof GRANT_LEVELS)[number];

export const LEVEL_LABELS: Record<PermissionLevel, string> = {
  none: "Geen",
  read: "Lezen",
  edit: "Bewerken",
  full: "Volledig",
};

const LEVEL_RANK: Record<PermissionLevel, number> = { none: 0, read: 1, edit: 2, full: 3 };

export function levelRank(level: PermissionLevel): number {
  return LEVEL_RANK[level] ?? 0;
}

/** The capability level an HTTP method requires: read for GET/HEAD, full for DELETE, edit otherwise. */
export function methodToLevel(method: string): PermissionLevel {
  const m = method.toUpperCase();
  if (m === "GET" || m === "HEAD" || m === "OPTIONS") return "read";
  if (m === "DELETE") return "full";
  return "edit"; // POST / PUT / PATCH
}

/** A full capability map (every resource -> level). */
export type Capabilities = Record<StaffResource, PermissionLevel>;

export function emptyCapabilities(): Capabilities {
  return Object.fromEntries(STAFF_RESOURCES.map((r) => [r, "none"])) as Capabilities;
}

export function fullCapabilities(): Capabilities {
  return Object.fromEntries(STAFF_RESOURCES.map((r) => [r, "full"])) as Capabilities;
}
